/**
 * Get Data about a third party source via JSONP
 *
 * Ported from client/third_party_data.js
 * @module
 */

var WindowEnv = require('../../env/window');

var AsyncRequest = require('../../utils/async_request');
var logger = require('../../utils/logger');
var Script = require('../../utils/script');

/**
 * Get the name for the callback function to use to store the data retrieved from the third party.
 *
 * @private
 * @param {string} integrationId
 */
function getNameForCallbackFunction(integrationId:string) {
  return 'optimizely_' + integrationId;
}

/**
 * Creates a globally-available callback function (a property on window) for the given third party audience integration,
 * to be used to store data retrieved from the third party via JSONP.
 *
 * @private
 * @param {string} callbackFunctionName
 * @param {string} sourceId
 * @returns {Promise} - Promise that resolves with loaded third party data
 */
function createCallbackFunction(callbackFunctionName:string, sourceId:string): Promise<any> {
  return AsyncRequest.makeAsyncRequest(sourceId, function() {
    WindowEnv.setGlobal(callbackFunctionName, function(thirdPartyInfo) {
      logger.debug('JSONP', 'Got response from', sourceId, thirdPartyInfo);

      AsyncRequest.resolveRequest(sourceId, thirdPartyInfo);

      // Clean up the callback function when it's no longer needed. Just set to undefined since 'delete' doesn't work in IE8.
      WindowEnv.setGlobal(callbackFunctionName, undefined);
    });
  });
}

/**
 * Constructs the URL to use to fetch data from the given third party.
 *
 * TODO(tyler): do this rendering server-side?
 *
 * @param {string} baseUrl
 * @param {string} cbParam
 */
exports.buildURL = function(baseUrl:string, cbParam:string) {
  return baseUrl + (baseUrl.indexOf('?') === -1 ? '?' : '&') + cbParam + '=%cb%';
};

/**
 * Creates a callback function and adds the script tag for a data provider whose data is retrieved via JSONP.
 *
 * The expectation is that retrieval is attempted only once per page load. Otherwise, the callback function may have
 * deleted itself by the time JSONP requests besides the first one come back.
 *
 * TODO(jon): Potentially extract part of this method into a generalized JSONP util.
 *
 * @param {string} sourceId
 * @param {string} url
 * @param {string} [callbackFunctionName]
 * @returns {Promise}
 */
exports.fetchData = function(sourceId:string, url:string, callbackFunctionName?:string): Promise<any> {
  if (!callbackFunctionName) {
    callbackFunctionName = getNameForCallbackFunction(sourceId);
  }

  if (WindowEnv.getGlobal(callbackFunctionName)) {
    // may have already been set by js/client, register a handler for the response
    logger.debug('JSONP / Found existing callback function for', sourceId);
    return AsyncRequest.makeAsyncRequest(sourceId);
  }

  // If no URL is provided, we won't create a request, but maybe there is already one out there that will result
  // in the callback being called.
  if (url) {
    Script.addScriptAsync(url.replace('%cb%', callbackFunctionName));
  }

  // Always create and return a callback function
  return createCallbackFunction(callbackFunctionName, sourceId);
};

